import { useTranslation } from '../contexts/LanguageContext';
import React, { useState, useEffect, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mapContainerStyle = { width: '100%', height: '100%' };
const defaultCenter = { lat: 42.6977, lng: 23.3219 }; // Sofia

function GoogleMapsReal({ properties, center, zoom = 13, singleMarker = false }) {
  const [apiKey, setApiKey] = useState(null);
  const [keyLoading, setKeyLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/site-settings/maps-key`)
      .then(r => setApiKey(r.data.google_maps_api_key || ''))
      .catch(() => setApiKey(''))
      .finally(() => setKeyLoading(false));
  }, []);

  if (keyLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-secondary rounded-xl">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!apiKey) {
    return <FallbackMap properties={properties} center={center} singleMarker={singleMarker} />;
  }

  return (
    <RealMap 
      apiKey={apiKey} 
      properties={properties} 
      center={center} 
      zoom={zoom} 
      singleMarker={singleMarker} 
    />
  );
}

function RealMap({ apiKey, properties, center, zoom, singleMarker }) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    language: 'bg'
  });

  const [selected, setSelected] = useState(null);
  const [map, setMap] = useState(null);

  const onLoad = useCallback((map) => {
    setMap(map);
    if (!singleMarker) {
      const validProps = properties?.filter(p => p.latitude && p.longitude) || [];
      if (validProps.length > 1) {
        // Множество точки — fitBounds обхваща всички
        const bounds = new window.google.maps.LatLngBounds();
        validProps.forEach(p => bounds.extend({ lat: p.latitude, lng: p.longitude }));
        map.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 });
      } else if (validProps.length === 1) {
        // Само една точка — центрирай с zoom 13
        map.setCenter({ lat: validProps[0].latitude, lng: validProps[0].longitude });
        map.setZoom(13);
      } else {
        // Няма точки — покажи цяла България
        map.setCenter({ lat: 42.7339, lng: 25.4858 });
        map.setZoom(7);
      }
    }
  }, [properties, singleMarker]);

  if (loadError) {
    return <FallbackMap properties={properties} center={center} singleMarker={singleMarker} />;
  }

  if (!isLoaded) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-secondary rounded-xl">
        <div className="spinner"></div>
      </div>
    );
  }

  const mapCenter = center || defaultCenter;

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={mapCenter}
      zoom={zoom}
      onLoad={onLoad}
      options={{
        styles: [
          { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
          { featureType: 'transit', elementType: 'labels', stylers: [{ visibility: 'off' }] }
        ],
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true
      }}
    >
      {singleMarker && center && (
        <Marker position={center} />
      )}

      {!singleMarker && properties?.map((property) => {
        if (!property.latitude || !property.longitude) return null;
        return (
          <Marker
            key={property.id}
            position={{ lat: property.latitude, lng: property.longitude }}
            onClick={() => setSelected(property)}
            icon={{
              url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42">
                  <path d="M16 0C7.2 0 0 7.2 0 16c0 12 16 26 16 26s16-14 16-26C32 7.2 24.8 0 16 0z" fill="#1B5E37"/>
                  <circle cx="16" cy="16" r="8" fill="white"/>
                  <circle cx="16" cy="16" r="4" fill="#1B5E37"/>
                </svg>`
              )}`,
              scaledSize: new window.google.maps.Size(32, 42),
              anchor: new window.google.maps.Point(16, 42)
            }}
          />
        );
      })}

      {selected && (
        <InfoWindow
          position={{ lat: selected.latitude, lng: selected.longitude }}
          onCloseClick={() => setSelected(null)}
        >
          <div className="p-1 max-w-[240px]">
            {selected.main_image && (
              <img 
                src={selected.main_image} 
                alt={selected.name}
                className="w-full h-28 object-cover rounded-lg mb-2"
              />
            )}
            <h4 className="font-semibold text-sm mb-1">{selected.name}</h4>
            <p className="text-xs text-gray-600 mb-2">{selected.location}</p>
            <Link 
              to={`/properties/${selected.id}`}
              className="block text-center bg-[#1B5E37] text-white py-1.5 rounded text-xs font-medium hover:bg-[#154a2c] transition-colors"
            >
              Виж детайли
            </Link>
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  );
}

// Fallback: Google Maps iframe when no API key is set
function FallbackMap({ properties, center, singleMarker }) {
  const { t } = useTranslation();
  const lat = center?.lat || defaultCenter.lat;
  const lng = center?.lng || defaultCenter.lng;

  if (singleMarker) {
    return (
      <iframe 
        src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2933.7!2d${lng}!3d${lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2z!5e0!3m2!1sbg!2sbg!4v1704067200000!5m2!1sbg!2sbg`}
        width="100%" 
        height="100%" 
        style={{ border: 0 }} 
        allowFullScreen="" 
        loading="lazy"
        title="Location"
      />
    );
  }

  // Fallback без API key — Google iframe с fitBounds на всички точки
  const validProps = properties?.filter(p => p.latitude && p.longitude) || [];

  // Изчисли центъра и zoom за да обхване всички точки
  let iframeSrc;
  if (validProps.length === 0) {
    // Няма точки — покажи цяла България
    iframeSrc = `https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d1800000!2d25.4858!3d42.7339!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sbg!2sbg!4v1704067200000!5m2!1sbg!2sbg`;
  } else if (validProps.length === 1) {
    const p = validProps[0];
    iframeSrc = `https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d5000!2d${p.longitude}!3d${p.latitude}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sbg!2sbg!4v1704067200000!5m2!1sbg!2sbg`;
  } else {
    // Изчисли bbox на всички точки
    const minLat = Math.min(...validProps.map(p => p.latitude));
    const maxLat = Math.max(...validProps.map(p => p.latitude));
    const minLng = Math.min(...validProps.map(p => p.longitude));
    const maxLng = Math.max(...validProps.map(p => p.longitude));
    const centerLat = (minLat + maxLat) / 2;
    const centerLng = (minLng + maxLng) / 2;
    // Изчисли zoom от bbox размера
    const latDiff = maxLat - minLat;
    const lngDiff = maxLng - minLng;
    const maxDiff = Math.max(latDiff, lngDiff);
    let zoom = 12;
    if (maxDiff > 5) zoom = 6;
    else if (maxDiff > 2) zoom = 8;
    else if (maxDiff > 0.5) zoom = 10;
    else if (maxDiff > 0.1) zoom = 12;
    else zoom = 13;
    // Изчисли d стойност за iframe (метри)
    const d = Math.max(maxDiff * 111000 * 1.5, 5000);
    iframeSrc = `https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d${Math.round(d)}!2d${centerLng}!3d${centerLat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sbg!2sbg!4v1704067200000!5m2!1sbg!2sbg`;
  }

  return (
    <div className="relative w-full h-full">
      <iframe
        src={iframeSrc}
        width="100%"
        height="100%"
        style={{ border: 0 }}
        allowFullScreen=""
        loading="lazy"
        title={t('map.title')}
      />
      {/* Overlay маркери върху iframe */}
      {validProps.length > 0 && (
        <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg shadow-sm z-10">
          <p className="text-xs font-medium text-[#1B5E37]">📍 {validProps.length} {validProps.length === 1 ? t('map.project') : t('map.projects')}</p>
        </div>
      )}
    </div>
  );
}

export default GoogleMapsReal;
